import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, fmtMoney, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Trash, UserCircle, PencilSimple, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import ContactInvoices from "@/pages/ContactInvoices";
import ContactStatement from "@/pages/ContactStatement";
import { useAuth } from "@/context/AuthContext";

export default function Contacts() {
  const qc = useQueryClient();
  const { isAdmin } = useAuth();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ name: "", kind: "customer", phone: "", email: "", note: "" });
  const [invoicedInput, setInvoicedInput] = useState("");
  const [search, setSearch] = useState("");

  const { data: contacts = [] } = useQuery({
    queryKey: ["contacts"],
    queryFn: async () => (await api.get("/contacts")).data,
  });

  const q = search.trim().toLocaleLowerCase("tr-TR");
  const filteredContacts = q
    ? contacts.filter((c) => (c.name || "").toLocaleLowerCase("tr-TR").includes(q))
    : contacts;

  const { data: ledger } = useQuery({
    queryKey: ["ledger", selected?.id],
    queryFn: async () => (await api.get(`/contacts/${selected.id}/ledger`)).data,
    enabled: !!selected,
  });

  useEffect(() => {
    if (ledger?.contact) {
      setInvoicedInput(String(ledger.invoiced_total ?? 0));
    }
  }, [ledger?.contact?.id, ledger?.invoiced_total]);

  const saveInvoiced = useMutation({
    mutationFn: async ({ id, value, c }) =>
      (await api.put(`/contacts/${id}`, {
        name: c.name,
        kind: c.kind === "supplier" ? "supplier" : "customer",
        phone: c.phone || null,
        email: c.email || null,
        note: c.note || null,
        invoiced_total: value,
      })).data,
    onSuccess: () => {
      qc.invalidateQueries();
      toast.success("Fatura tutarı güncellendi");
    },
  });

  const [editing, setEditing] = useState(null);
  const resetForm = () => setForm({ name: "", kind: "customer", phone: "", email: "", note: "" });

  const create = useMutation({
    mutationFn: async (p) => (await api.post("/contacts", p)).data,
    onSuccess: () => {
      qc.invalidateQueries(); setOpen(false); setEditing(null); resetForm();
      toast.success("Cari eklendi");
    },
  });
  const update = useMutation({
    mutationFn: async ({ id, p }) => (await api.put(`/contacts/${id}`, p)).data,
    onSuccess: (data) => {
      qc.invalidateQueries(); setOpen(false); setEditing(null); resetForm();
      if (selected && data?.id === selected.id) setSelected(data);
      toast.success("Cari güncellendi");
    },
  });
  const remove = useMutation({
    mutationFn: async (id) => (await api.delete(`/contacts/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries(); setSelected(null); },
  });

  const startEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name || "", kind: (c.kind === "supplier" ? "supplier" : "customer"),
      phone: c.phone || "", email: c.email || "", note: c.note || "",
    });
    setOpen(true);
  };
  const startCreate = () => { setEditing(null); resetForm(); setOpen(true); };

  const submit = (e) => {
    e.preventDefault();
    if (editing) update.mutate({ id: editing.id, p: form });
    else create.mutate(form);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="contacts-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="overline">Müşteriler & Tedarikçiler</div>
          <h1 className="mt-1 text-3xl font-black tracking-tight">Cari Hesaplar</h1>
        </div>
        {isAdmin && (
          <Button data-testid="contact-add-btn" onClick={startCreate}>
            <Plus size={16} className="mr-1" /> Yeni Cari
          </Button>
        )}
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editing ? "Cari Düzenle" : "Yeni Cari Hesap"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <Label>İsim / Firma</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" />
              </div>
              <div>
                <Label>Cari Tipi</Label>
                <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                  <SelectTrigger data-testid="contact-kind"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="customer">Müşteri (bana ödeme yapan)</SelectItem>
                    <SelectItem value="supplier">Tedarikçi (benim ödeme yaptığım)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Telefon</Label>
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                </div>
                <div>
                  <Label>E-posta</Label>
                  <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>Not</Label>
                <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
              </div>
              <DialogFooter>
                <Button type="submit" data-testid="contact-save">{editing ? "Güncelle" : "Kaydet"}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="rounded-md border border-border bg-white lg:col-span-1">
          <div className="sticky top-0 z-10 border-b border-border bg-white p-2">
            <div className="relative">
              <MagnifyingGlass
                size={14}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cari ara..."
                className="h-8 pl-8 text-sm"
                data-testid="contacts-search"
              />
            </div>
          </div>
          {filteredContacts.map((c) => {
            const bal = Number(c.debt_balance || 0);
            const balClass = bal > 0
              ? "text-emerald-700"
              : bal < 0
                ? "text-red-600"
                : "text-muted-foreground";
            return (
              <div
                key={c.id}
                className={`flex w-full items-center justify-between border-b border-border/60 px-2 py-2 text-sm hover:bg-secondary ${
                  selected?.id === c.id ? "bg-secondary" : ""
                }`}
              >
                <button
                  onClick={() => setSelected(c)}
                  data-testid={`contact-row-${c.id}`}
                  className="flex flex-1 items-center gap-3 px-2 py-1 text-left"
                >
                  <UserCircle size={20} weight="duotone" className="text-muted-foreground" />
                  <div className="flex-1">
                    <div className="font-medium">{c.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {c.kind === "supplier" ? "Tedarikçi" : "Müşteri"}
                    </div>
                  </div>
                  <div className={`mono text-right text-sm font-semibold ${balClass}`}
                    data-testid={`contact-balance-${c.id}`}>
                    {bal > 0 ? "+" : ""}{fmtMoney(bal)}
                  </div>
                </button>
                {isAdmin && <button
                  onClick={(e) => { e.stopPropagation(); startEdit(c); }}
                  data-testid={`edit-contact-${c.id}`}
                  className="rounded border border-border p-1.5 text-foreground hover:bg-white"
                  title="Düzenle"
                >
                  <PencilSimple size={14} />
                </button>}
              </div>
            );
          })}
          {contacts.length === 0 && (
            <div className="py-8 text-center text-sm text-muted-foreground">Henüz cari yok.</div>
          )}
          {contacts.length > 0 && filteredContacts.length === 0 && (
            <div className="py-8 text-center text-sm text-muted-foreground" data-testid="contacts-search-empty">
              "{search}" ile eşleşen cari yok.
            </div>
          )}
        </div>

        <div className="rounded-md border border-border bg-white lg:col-span-2">
          {!selected && (
            <div className="flex h-full min-h-[300px] items-center justify-center text-sm text-muted-foreground">
              Detayları görmek için bir cari seçin.
            </div>
          )}
          {selected && ledger && (
            <div className="p-6" data-testid="contact-detail">
              <div className="flex items-start justify-between">
                <div>
                  <div className="overline">Cari Detay</div>
                  <div className="mt-1 text-2xl font-semibold">{ledger.contact?.name}</div>
                  <div className="text-sm text-muted-foreground">
                    <span className="mr-2 inline-block rounded-sm bg-secondary px-2 py-0.5 text-xs">
                      {ledger.kind === "supplier" ? "Tedarikçi" : "Müşteri"}
                    </span>
                    {ledger.contact?.phone} · {ledger.contact?.email}
                  </div>
                </div>
                {isAdmin && <button
                  onClick={() => remove.mutate(selected.id)}
                  data-testid={`del-contact-${selected.id}`}
                  className="text-destructive hover:opacity-70"
                >
                  <Trash size={18} />
                </button>}
              </div>
              <Tabs defaultValue="genel" className="mt-4">
                <TabsList data-testid="contact-tabs">
                  <TabsTrigger value="genel" data-testid="tab-genel">Genel</TabsTrigger>
                  <TabsTrigger value="faturalar" data-testid="tab-faturalar">Faturalar</TabsTrigger>
                  <TabsTrigger value="ekstre" data-testid="tab-ekstre">Ekstre</TabsTrigger>
                </TabsList>
                <TabsContent value="genel" className="mt-4">
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <div className="rounded-md border border-border p-4">
                  <div className="overline">Kesilen Toplam Fatura (TL)</div>
                  <div className="mt-2 flex items-center gap-2">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={invoicedInput}
                      onChange={(e) => setInvoicedInput(e.target.value)}
                      readOnly={!isAdmin}
                      data-testid="invoiced-input"
                      className="mono flex-1 rounded-md border border-input bg-white px-2 py-1 text-lg font-semibold outline-none focus:ring-2 focus:ring-primary"
                    />
                    {isAdmin && <Button
                      size="sm"
                      onClick={() =>
                        saveInvoiced.mutate({
                          id: selected.id,
                          value: parseFloat(invoicedInput || 0),
                          c: ledger.contact,
                        })
                      }
                      disabled={!ledger?.contact || saveInvoiced.isPending}
                      data-testid="invoiced-save"
                    >
                      Kaydet
                    </Button>}
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    Cariye kesilen faturaların toplamı
                  </div>
                </div>
                <div className="rounded-md border border-border p-4">
                  <div className="overline">Yapılan Ödemeler (TL)</div>
                  <div className="mono mt-2 text-lg font-semibold text-emerald-700" data-testid="paid-total">
                    {fmtMoney(ledger.paid_total_try || 0)}
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    Bu cariye ait ödenmiş tüm işlemler
                  </div>
                </div>
                <div className="rounded-md border border-border p-4">
                  <div className="overline">Borç Bakiyesi (TL)</div>
                  <div
                    className={`mono mt-2 text-2xl font-black ${
                      (ledger.debt_balance || 0) > 0
                        ? "text-red-600"
                        : (ledger.debt_balance || 0) < 0
                          ? "text-amber-700"
                          : "text-emerald-700"
                    }`}
                    data-testid="debt-balance"
                  >
                    {fmtMoney(ledger.debt_balance || 0)}
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {(ledger.debt_balance || 0) > 0
                      ? "Kalan borç"
                      : (ledger.debt_balance || 0) < 0
                        ? "Fazla ödeme"
                        : "Kapalı"}
                  </div>
                </div>
              </div>
              <div className="mt-4 rounded-md border border-border p-4">
                <div className="overline">Bekleyen Bakiye (TL)</div>
                <div
                  className={`mono mt-1 text-2xl font-semibold ${
                    ledger.balance_try >= 0 ? "text-emerald-700" : "text-red-600"
                  }`}
                >
                  {fmtMoney(ledger.balance_try)}
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {ledger.balance_try >= 0 ? "Alacaklı" : "Borçlu"}
                </div>
              </div>
              <div className="mt-6">
                <div className="overline mb-2">Hareketler</div>
                <div className="overflow-x-auto">
                <table className="w-full min-w-[560px] text-sm">
                  <thead className="border-b border-border">
                    <tr className="text-left text-xs uppercase text-muted-foreground">
                      <th className="py-2">Tarih</th>
                      <th>Tip</th>
                      <th>Kategori</th>
                      <th>Açıklama</th>
                      <th className="text-right">Tutar</th>
                      <th>Durum</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ledger.transactions.map((t) => (
                      <tr key={t.id} className="border-b border-border/60">
                        <td className="py-2 mono">{fmtDate(t.date)}</td>
                        <td>{t.kind === "income" ? "Gelir" : "Gider"}</td>
                        <td>
                          {t.category ? (
                            <span className="inline-block rounded-sm bg-secondary px-2 py-0.5 text-xs">
                              {t.category}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </td>
                        <td className="text-muted-foreground">{t.description || "-"}</td>
                        <td className="text-right mono">{fmtMoney(t.amount, t.currency)}</td>
                        <td>
                          <span className={`text-xs ${t.status === "paid" ? "text-emerald-700" : "text-amber-700"}`}>
                            {t.status === "paid" ? "Ödendi" : "Bekliyor"}
                          </span>
                        </td>
                      </tr>
                    ))}
                    {ledger.transactions.length === 0 && (
                      <tr><td colSpan={6} className="py-6 text-center text-muted-foreground">Hareket yok.</td></tr>
                    )}
                  </tbody>
                </table>
                </div>
              </div>
                </TabsContent>
                <TabsContent value="faturalar" className="mt-4">
                  <ContactInvoices contact={ledger.contact} />
                </TabsContent>
                <TabsContent value="ekstre" className="mt-4">
                  <ContactStatement contact={ledger.contact} />
                </TabsContent>
              </Tabs>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
